/**
 * Which resolutions a gallery offers for download.
 *
 * `''` (the default) means "all resolutions". The setting is **purely declarative**:
 * the download endpoints do not check it, so every place that offers a download has to
 * resolve the allowed qualities through `resolveDownloadQualities()` instead of
 * hard-coding both buttons.
 *
 * Lives on the drive (`HeyDrive.download_options`, editable by the photographer) and is
 * mirrored read-only into the public view payloads (share, selection, portfolio).
 */
export type DownloadOptions = '' | 'detail' | 'original'

/**
 * Quality value the (multi-)download endpoints expect.
 * `high` = the web-optimized `detail` version, `original` = the file as uploaded.
 */
export type DownloadQuality = 'high' | 'original'

/** Every quality, in the order it is offered. */
export const ALL_DOWNLOAD_QUALITIES: DownloadQuality[] = ['high', 'original']

export interface DownloadOptionsInput {
  /** The drive's `download_options`. `null`/`undefined` (older backends) counts as `''`. */
  downloadOptions?: DownloadOptions | null
  /**
   * Whether a watermark is active on the drive. A watermark **wins over**
   * `downloadOptions`: originals are never handed out, only the (watermarked) detail
   * version. Unknown (`undefined`) is treated as "no watermark" — the public share /
   * portfolio payloads do not carry the flag, see `resolveDownloadQualities` callers.
   */
  hasWatermark?: boolean
}

/**
 * The single source of truth for which download resolutions may be offered.
 *
 * Add new restrictions here, never inline at a call site — the same answer has to hold
 * for the selection bar, the gallery-navigation wizard and the single-file download.
 */
export function resolveDownloadQualities({ downloadOptions, hasWatermark }: DownloadOptionsInput): DownloadQuality[] {
  // A watermark only exists on the detail version, so originals are off the table.
  if (hasWatermark) {
    return ['high']
  }
  switch (downloadOptions) {
    case 'detail':
      return ['high']
    case 'original':
      return ['original']
    default:
      return [...ALL_DOWNLOAD_QUALITIES]
  }
}

/** Convenience wrapper for the (partial) drive shape held in the stores. */
export function resolveDriveDownloadQualities(
  drive?: { download_options?: DownloadOptions | null, watermark?: string | null } | null
): DownloadQuality[] {
  return resolveDownloadQualities({
    downloadOptions: drive?.download_options,
    hasWatermark: !!drive?.watermark,
  })
}

/**
 * Which variant a *single-file* download hands out. There is no picker for single files,
 * so a restriction decides on its own; with both qualities still allowed the drive's
 * legacy `use_details_dl` flag decides exactly as before.
 */
export function resolveSingleFileQuality(qualities: DownloadQuality[], useDetailsDl?: boolean): DownloadQuality {
  if (!qualities.includes('original')) {
    return 'high'
  }
  if (!qualities.includes('high')) {
    return 'original'
  }
  return useDetailsDl ? 'high' : 'original'
}
